<template>
  <div class="measure-mapping-select">
    <b-field
      :label="label"
      class="input-field">
      <b-select
        v-model="mapping"
        :loading="loading"
        expanded>
        <option :value="null">
          {{ noneLabel }}
        </option>
        <option
          v-for="option in options"
          :key="option.key"
          :value="option.key">
          {{ option.name }}
        </option>
      </b-select>
    </b-field>

    <p class="section-help">
      {{ help }}
    </p>

    <p
      v-if="mapping && !loading && !selected"
      class="section-help is-warning">
      This report names a mapping that no longer exists for this company.
      Every row using it will be left out until it is pointed at one that
      does.
    </p>

    <p
      v-else-if="coverageLoading"
      class="section-help">
      Checking which measures resolve…
    </p>

    <template v-else-if="coverageText">
      <p class="section-help">
        {{ coverageText }}
      </p>
      <p
        v-for="reason in coverageReasons"
        :key="reason"
        class="section-help is-muted">
        {{ reason }}
      </p>
    </template>
  </div>
</template>

<script>
  export default {
    name: 'MeasureMappingSelect',
    props: {
      // The measure_config this mapping belongs to. Bound as an object rather
      // than to the string directly so the key can be removed entirely when
      // set back to none, instead of being persisted as an explicit null.
      value: {
        type: Object,
        default: null,
      },
      label: {
        type: String,
        default: 'Measure mapping',
      },
      noneLabel: {
        type: String,
        default: 'None — use the KPI measure itself',
      },
      help: {
        type: String,
        default: '',
      },
      // The module's KPI measures. Sent to the coverage endpoint to find out
      // which of them survive the chosen mapping. Optional: without them
      // there is nothing to ask about, and the picker just says less.
      kpiMeasures: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        options: [],
        loading: false,
        coverage: null,
        coverageLoading: false,
        // Coverage is refetched whenever the mapping or the module's measures
        // change, so a slow earlier request can land after a newer one. Only
        // the newest request is allowed to write its result.
        coverageRequest: 0,
      }
    },
    computed: {
      mapping: {
        get () {
          return this.value?.company_measure_mapping || null
        },
        set (key) {
          // `measure_config` is nullable on a row, so selecting a mapping on
          // a row that has none yet has to create one.
          const config = { ...(this.value || {}), }
          if (key) {
            config.company_measure_mapping = key
          } else {
            delete config.company_measure_mapping
          }
          this.$emit('input', config)
        },
      },

      selected () {
        return this.options.find((option) => option.key === this.mapping)
      },

      // Resolved server-side: a measure the mapping does not name can still
      // resolve through its own definition, so name-matching here would
      // report working rows as lost.
      coverageText () {
        const measures = this.coverage?.measures
        if (!measures || !measures.length) return ''

        const lost = measures.filter((m) => !m.resolves)
        if (!lost.length) {
          return measures.length === 1
            ? 'This measure resolves through the mapping.'
            : `All ${measures.length} measures resolve through this mapping.`
        }
        if (lost.length === measures.length) {
          return 'No measure in this module resolves through this mapping,'
            + ' so every card will lose this row.'
        }
        return `${measures.length - lost.length} of ${measures.length} measures`
          + ` resolve. ${this.nameList(lost)} will lose this row.`
      },

      // The distinct last line of each failing measure's trace: the reason
      // it could not resolve.
      coverageReasons () {
        const lost = (this.coverage?.measures || []).filter((m) => !m.resolves)
        const reasons = lost
          .map((m) => m.trace?.[m.trace.length - 1])
          .filter(Boolean)
        return [...new Set(reasons),]
      },
    },
    watch: {
      mapping: {
        immediate: true,
        handler () {
          this.loadCoverage()
        },
      },
      // Adding or repointing a KPI measure changes the answer, and the row
      // editor stays open across those edits.
      kpiMeasures: {
        deep: true,
        handler () {
          this.loadCoverage()
        },
      },
    },
    async mounted () {
      this.loading = true
      const response = await this.$apis.companyConfigs.content.measureMappings.list()
      this.options = Array.isArray(response) ? response : []
      this.loading = false
    },
    methods: {
      nameList (measures) {
        return measures.map((m) => m.name || 'Untitled').join(', ')
      },

      async loadCoverage () {
        if (!this.mapping || !this.kpiMeasures.length) {
          // Take a ticket even when there is nothing to fetch: an in-flight
          // request from the previous mapping would otherwise still hold the
          // newest ticket, land after this, and show coverage for a mapping
          // that is no longer selected.
          this.coverageRequest++
          this.coverage = null
          this.coverageLoading = false
          return
        }

        const request = ++this.coverageRequest
        this.coverageLoading = true
        const response = await this.$apis.companyConfigs.content.measureMappings.coverage(
          this.mapping,
          this.kpiMeasures.map((kpiMeasure) => ({
            name: kpiMeasure.name || '',
            measure: kpiMeasure.measure_config?.measure || null,
          }))
        )

        // A newer request has been issued since; its answer is the right one.
        if (request !== this.coverageRequest) return

        this.coverage = response?.measures ? response : null
        this.coverageLoading = false
      },
    },
  }
</script>

<style lang="scss" scoped>
.section-help.is-warning {
  color: $warning;
}

.section-help.is-muted {
  color: $grayscale-3;
}
</style>
